import { useState, useRef, ReactNode, DragEvent } from 'react';
import { Upload, X, FileIcon } from 'lucide-react';

export interface FileUploadProps {
  onFiles: (files: File[]) => void;
  accept?: string;
  multiple?: boolean;
  /** Max rozmiar per plik w bajtach. Default: 10MB. */
  maxSize?: number;
  /** Pokazuj listę wybranych plików pod strefą drop. Default: true. */
  showFileList?: boolean;
  label?: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
  className?: string;
}

const DEFAULT_MAX = 10 * 1024 * 1024;

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * Drag-and-drop strefa uploadu + kliknięcie żeby otworzyć file picker.
 * Walidacja rozmiaru, pokazuje listę wybranych plików z przyciskiem X.
 */
export function FileUpload({
  onFiles,
  accept,
  multiple = false,
  maxSize = DEFAULT_MAX,
  showFileList = true,
  label = 'Przeciągnij pliki tutaj lub kliknij',
  description,
  disabled = false,
  className,
}: FileUploadProps) {
  const [dragOver, setDragOver] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFiles = (list: FileList | null) => {
    if (!list || list.length === 0) return;
    const arr = Array.from(list);
    const oversized = arr.find(f => f.size > maxSize);
    if (oversized) {
      setError(`Plik "${oversized.name}" jest zbyt duży (max ${formatBytes(maxSize)})`);
      return;
    }
    setError(null);
    const next = multiple ? [...files, ...arr] : arr.slice(0, 1);
    setFiles(next);
    onFiles(next);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled) return;
    handleFiles(e.dataTransfer.files);
  };

  const removeFile = (idx: number) => {
    const next = files.filter((_, i) => i !== idx);
    setFiles(next);
    onFiles(next);
  };

  return (
    <div className={className}>
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={() => !disabled && inputRef.current?.click()}
        onKeyDown={(e) => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) inputRef.current?.click(); }}
        onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        className={[
          'flex flex-col items-center justify-center gap-2 p-8 rounded-xl border-2 border-dashed transition-colors duration-fast text-center cursor-pointer',
          dragOver
            ? 'border-accent-primary bg-accent-primary/5'
            : 'border-border hover:border-accent-primary/40 hover:bg-surface-hover/20',
          disabled ? 'opacity-50 cursor-not-allowed' : '',
        ].filter(Boolean).join(' ')}
      >
        <Upload className="w-8 h-8 text-text-muted" />
        <div className="text-sm text-text-secondary font-medium">{label}</div>
        {description && <div className="text-xs text-text-muted">{description}</div>}
        <div className="text-xs text-text-disabled">Max {formatBytes(maxSize)} {multiple ? '(można kilka)' : ''}</div>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          disabled={disabled}
          onChange={(e) => handleFiles(e.target.files)}
          className="sr-only"
        />
      </div>

      {error && (
        <p className="mt-2 text-xs text-danger" role="alert">{error}</p>
      )}

      {showFileList && files.length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {files.map((f, idx) => (
            <li
              key={`${f.name}-${idx}`}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-surface-raised/40 border border-border-subtle text-sm"
            >
              <FileIcon className="w-4 h-4 text-text-muted shrink-0" />
              <span className="flex-1 min-w-0 truncate text-text-primary">{f.name}</span>
              <span className="text-xs text-text-muted shrink-0">{formatBytes(f.size)}</span>
              <button
                type="button"
                onClick={() => removeFile(idx)}
                className="shrink-0 inline-flex items-center justify-center w-6 h-6 rounded text-text-muted hover:text-danger hover:bg-danger/10 transition-colors"
                aria-label={`Usuń ${f.name}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
